import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';

/**
 * Plaats / woonplaats autocomplete via PDOK suggest API.
 * - kind="woonplaats" (default) | "straatnaam" | "adres"
 * - Filtert dynamisch terwijl je typt; toont een dropdown met max 15 resultaten.
 *
 * Voor "woonplaats" tonen we alleen het eerste deel (de stadsnaam zelf) als suggestie,
 * en bij selectie sturen we { plaats, gemeente, provincie } naar onChange.
 */
export default function PlaatsAutocomplete({
  value,
  onChange,
  placeholder,
  kind = 'woonplaats',
  testid = 'plaats-autocomplete',
  className = 'cs-input',
  style,
  onPick,                // optional callback met volledige PDOK record bij selectie
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef();
  const blurRef = useRef();

  const runSuggest = React.useCallback((q) => {
    return Promise.resolve()
      .then(() => { setLoading(true); })
      .then(() => carsysApi.adresSuggest(q, kind))
      .then(rows => { setResults(rows); setHighlight(0); })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [kind]);

  useEffect(() => {
    if (!open) return;
    const q = (value || '').trim();
    if (q.length < 1) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { runSuggest(q); }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [value, open, runSuggest]);

  const pick = (row) => {
    const naam = (row.weergavenaam || '').split(',')[0].trim();
    onChange(naam);
    onPick?.(row, naam);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div style={{ position: 'relative', ...style }} data-testid={testid}>
      <div style={{ position: 'relative' }}>
        <input
          className={className}
          type="text"
          value={value || ''}
          placeholder={placeholder || t('Begin met typen...')}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => { blurRef.current = setTimeout(() => setOpen(false), 180); }}
          onKeyDown={handleKey}
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {loading && (
          <Loader2 size={14} className="spin" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        )}
      </div>
      {open && (results.length > 0 || loading) && (
        <ul
          className="pa-dropdown"
          data-testid={`${testid}-dropdown`}
          onMouseDown={() => clearTimeout(blurRef.current)}
        >
          {loading && results.length === 0 && <li className="pa-empty">{t('Bezig met zoeken...')}</li>}
          {results.map((r, i) => {
            const naam = (r.weergavenaam || '').split(',')[0];
            const rest = (r.weergavenaam || '').split(',').slice(1).join(',').trim();
            return (
              <li
                key={r.id || i}
                className={`pa-item ${i === highlight ? 'highlight' : ''}`}
                onClick={() => pick(r)}
                onMouseEnter={() => setHighlight(i)}
                data-testid={`${testid}-item-${i}`}
              >
                <MapPin size={12} style={{ color: 'var(--cs-text-muted)', flexShrink: 0 }} />
                <span className="pa-name">{naam}</span>
                {rest && <span className="pa-rest">{rest}</span>}
              </li>
            );
          })}
        </ul>
      )}
      <style>{`
        .pa-dropdown {
          list-style: none; margin: 4px 0 0; padding: 4px;
          position: absolute; left: 0; right: 0; top: 100%; z-index: 50;
          background: #fff; border: 1px solid var(--cs-border); border-radius: 8px;
          box-shadow: 0 8px 24px rgba(15,23,42,.12);
          max-height: 280px; overflow-y: auto;
        }
        .pa-item {
          padding: 7px 10px; border-radius: 5px; cursor: pointer;
          display: flex; align-items: center; gap: 8px; font-size: 13px;
        }
        .pa-item.highlight, .pa-item:hover {
          background: var(--cs-accent-soft); color: var(--cs-accent);
        }
        .pa-name { font-weight: 600; flex-shrink: 0; }
        .pa-rest { color: var(--cs-text-muted); font-size: 11.5px; }
        .pa-empty { padding: 10px; color: var(--cs-text-muted); font-size: 12px; text-align: center; }
        .spin { animation: spin 800ms linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
